/**
 * Dates, in Asia/Singapore.
 *
 * The API speaks two kinds: a **calendar date** (`YYYY-MM-DD`) for anything a person picked —
 * an event date, a deadline — and an **instant** (ISO, UTC) for anything that happened. They
 * must be rendered differently: a calendar date has no timezone to convert, so passing
 * `2026-08-20` through `new Date()` and formatting it in SGT is how a date shifts by a day.
 */

const SGT = 'Asia/Singapore';

const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-SG', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const INSTANT = new Intl.DateTimeFormat('en-SG', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: SGT,
});

/** `20 Aug 2026`, from a calendar date. Formatted as UTC so the day cannot shift. */
export function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? date : DAY_MONTH_YEAR.format(parsed);
}

/** `20 Aug 2026, 18:27` in Singapore, from an ISO instant. */
export function formatInstant(instant: string | Date | null | undefined): string {
  if (!instant) return '—';
  const parsed = instant instanceof Date ? instant : new Date(instant);
  return Number.isNaN(parsed.getTime()) ? String(instant) : INSTANT.format(parsed);
}

/** Today in Singapore, as a calendar date — the same definition the API's `sgtToday` uses. */
export function sgtToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: SGT,
  }).format(now);
  return parts;
}

/** Whole days from `from` to `to`, both calendar dates. Negative when `to` is in the past. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

export function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

export interface Countdown {
  days: number;
  /** `in 6 days` / `today` / `4 days overdue` — a deadline is a date *and* a countdown. */
  label: string;
  overdue: boolean;
  /** Inside a week, or already past: worth colouring. */
  urgent: boolean;
}

/**
 * A deadline as both facts.
 *
 * "By 3 September" alone makes people count on their fingers; "in 6 days" alone hides which
 * date to put in the calendar. The screens show both.
 */
export function countdownTo(deadline: string, today: string = sgtToday()): Countdown {
  const days = daysBetween(today, deadline);
  const overdue = days < 0;
  const label = overdue
    ? `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} overdue`
    : days === 0
      ? 'today'
      : `in ${days} ${days === 1 ? 'day' : 'days'}`;
  return { days, label, overdue, urgent: overdue || days <= 7 };
}

const WITH_WEEKDAY = new Intl.DateTimeFormat('en-SG', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * `Thursday, 10 September 2026` — the unambiguous echo of a date input.
 *
 * `<input type="date">` renders in the browser's locale and no attribute changes that, so a
 * Singapore user on a US-locale browser is shown `09/10/2026` for the tenth of September. The
 * weekday is worth having anyway: nobody plans a team dinner without knowing what day it is.
 */
export function formatDateLong(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? date : WITH_WEEKDAY.format(parsed);
}
